import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "../css/career-scoped.css";

// Icons
import { 
  FaUsers, 
  FaRocket, 
  FaShieldAlt, 
  FaHeart,
  FaBriefcase,
  FaMapMarkerAlt,
  FaClock,
  FaMoneyBillWave,
  FaLaptop,
  FaGraduationCap,
  FaHeartbeat,
  FaUmbrella,
  FaCoffee,
  FaTrophy,
  FaHandsHelping,
  FaChartLine,
  FaUserTie,
  FaEnvelope
} from "react-icons/fa";

const Career = () => {
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");

  const jobOpenings = [
    {
      id: 1,
      title: "Senior Backend Developer",
      department: "Engineering",
      location: "Remote / Bangalore",
      type: "Full-time",
      experience: "3-5 years",
      description: "Join our engineering team to build scalable hosting infrastructure and APIs.",
      requirements: [
        "Strong experience with Node.js, Python, or PHP",
        "Experience with cloud platforms (AWS, Azure, GCP)",
        "Knowledge of database management and optimization",
        "Understanding of web hosting technologies"
      ]
    },
    {
      id: 2,
      title: "DevOps Engineer",
      department: "Engineering",
      location: "Remote / Mumbai",
      type: "Full-time",
      experience: "2-4 years",
      description: "Maintain and optimize our hosting infrastructure across multiple data centers.",
      requirements: [
        "Experience with Docker, Kubernetes, and containerization",
        "Strong Linux/Unix administration skills",
        "Knowledge of CI/CD pipelines and automation",
        "Experience with monitoring tools (Prometheus, Grafana)"
      ]
    },
    {
      id: 3,
      title: "Customer Support Specialist",
      department: "Support",
      location: "Remote / Delhi",
      type: "Full-time",
      experience: "1-3 years",
      description: "Provide exceptional technical support to our customers 24/7.",
      requirements: [
        "Strong communication skills in English and Hindi",
        "Basic understanding of web hosting and cPanel",
        "Customer-first attitude and problem-solving skills",
        "Ability to work in shifts (24/7 support)"
      ]
    },
    {
      id: 4,
      title: "Digital Marketing Manager",
      department: "Marketing",
      location: "Remote / Bangalore",
      type: "Full-time",
      experience: "3-5 years",
      description: "Lead our digital marketing initiatives and grow our online presence.",
      requirements: [
        "Proven experience in digital marketing and SEO",
        "Strong understanding of SaaS/hosting industry",
        "Experience with Google Ads, Facebook Ads, and analytics",
        "Creative content creation and campaign management skills"
      ]
    },
    {
      id: 5,
      title: "Frontend Developer",
      department: "Engineering",
      location: "Remote / Pune",
      type: "Full-time",
      experience: "2-4 years",
      description: "Build beautiful and responsive user interfaces for our hosting platform.",
      requirements: [
        "Strong experience with React.js and modern JavaScript",
        "Knowledge of HTML5, CSS3, and responsive design",
        "Experience with REST APIs and state management",
        "Understanding of UI/UX best practices"
      ]
    },
    {
      id: 6,
      title: "Security Analyst",
      department: "Security",
      location: "Remote / Bangalore",
      type: "Full-time",
      experience: "3-6 years",
      description: "Ensure the security of our hosting infrastructure and customer data.",
      requirements: [
        "Strong knowledge of cybersecurity principles",
        "Experience with security tools and vulnerability assessment",
        "Understanding of web application security",
        "Certifications like CEH, CISSP are a plus"
      ]
    }
  ];

  const benefits = [
    {
      icon: FaMoneyBillWave,
      title: "Competitive Salary",
      description: "Industry-leading compensation packages with performance bonuses"
    },
    {
      icon: FaLaptop,
      title: "Remote Work",
      description: "Flexible work-from-home options and modern office spaces"
    },
    {
      icon: FaHeartbeat,
      title: "Health Insurance",
      description: "Comprehensive health coverage for you and your family"
    },
    {
      icon: FaGraduationCap,
      title: "Learning & Development",
      description: "Annual training budget and access to online learning platforms"
    },
    {
      icon: FaUmbrella,
      title: "Paid Time Off",
      description: "Generous vacation policy and paid holidays"
    },
    {
      icon: FaCoffee,
      title: "Work-Life Balance",
      description: "Flexible working hours and supportive team environment"
    },
    {
      icon: FaTrophy,
      title: "Growth Opportunities",
      description: "Clear career progression paths and internal promotions"
    },
    {
      icon: FaHandsHelping,
      title: "Team Events",
      description: "Regular team building activities and company celebrations"
    }
  ];

  const cultureValues = [
    {
      icon: FaHeart,
      title: "Customer Obsession",
      description: "We're passionate about helping our customers succeed online"
    },
    {
      icon: FaRocket,
      title: "Innovation",
      description: "We encourage creative thinking and embrace new technologies"
    },
    {
      icon: FaUsers,
      title: "Collaboration",
      description: "We work together as one team towards common goals"
    },
    {
      icon: FaShieldAlt,
      title: "Integrity",
      description: "We operate with transparency, honesty, and ethical standards"
    }
  ];

  const departments = ["all", "Engineering", "Support", "Marketing", "Security"];
  const locations = ["all", "Remote", "Bangalore", "Mumbai", "Delhi", "Pune"];

  const filteredJobs = jobOpenings.filter(job => {
    const departmentMatch = selectedDepartment === "all" || job.department === selectedDepartment;
    const locationMatch = selectedLocation === "all" || job.location.includes(selectedLocation);
    return departmentMatch && locationMatch;
  });

  return (
    <div className="career-page-wrapper">
      <Helmet>
        <title>Careers at ClubHosty - Join Our Growing Team</title>
        <meta
          name="description"
          content="Explore exciting career opportunities at ClubHosty. Join our team and help build the future of web hosting. We offer competitive salaries, remote work, and growth opportunities."
        />
        <meta name="keywords" content="ClubHosty careers, web hosting jobs, tech jobs India, remote jobs, DevOps jobs, developer jobs" />
        <link rel="canonical" href="https://clubhosty.com/career" />
      </Helmet>

      {/* Hero Section */}
      <section className="career-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Build Your Career with ClubHosty</h1>
            <p className="hero-subtitle">
              Join a team that's revolutionizing web hosting in India. We're looking for passionate individuals who want to make a difference.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <FaUsers className="stat-icon" />
                <div className="stat-text">
                  <div className="stat-number">50+</div>
                  <div className="stat-label">Team Members</div>
                </div>
              </div>
              <div className="stat-item">
                <FaTrophy className="stat-icon" />
                <div className="stat-text">
                  <div className="stat-number">5000+</div>
                  <div className="stat-label">Happy Customers</div>
                </div>
              </div>
              <div className="stat-item">
                <FaChartLine className="stat-icon" />
                <div className="stat-text">
                  <div className="stat-number">4</div>
                  <div className="stat-label">Global Offices</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="career-culture">
        <div className="container">
          <div className="section-header">
            <h2>Our Culture & Values</h2>
            <p>We believe in creating an environment where innovation thrives and people grow</p>
          </div>
          <div className="culture-grid">
            {cultureValues.map((value, index) => (
              <div key={index} className="culture-card">
                <div className="culture-icon-wrapper">
                  <value.icon className="culture-icon" />
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="career-benefits">
        <div className="container">
          <div className="section-header">
            <h2>Why Join ClubHosty?</h2>
            <p>We offer more than just a job - we offer a career path and a supportive environment</p>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <benefit.icon className="benefit-icon" />
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Openings Section */}
      <section className="career-openings">
        <div className="container">
          <div className="section-header">
            <h2>Current Openings</h2>
            <p>Find your next opportunity and join our growing team</p>
          </div>

          {/* Filters */}
          <div className="job-filters">
            <div className="filter-group">
              <label>Department:</label>
              <select 
                value={selectedDepartment} 
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="filter-select"
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>
                    {dept === "all" ? "All Departments" : dept}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Location:</label>
              <select 
                value={selectedLocation} 
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="filter-select"
              >
                {locations.map(loc => (
                  <option key={loc} value={loc}>
                    {loc === "all" ? "All Locations" : loc}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Job Listings */}
          <div className="jobs-list">
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job) => (
                <div key={job.id} className="job-card">
                  <div className="job-header">
                    <div className="job-title-section">
                      <h3 className="job-title">{job.title}</h3>
                      <div className="job-meta">
                        <span className="job-meta-item">
                          <FaBriefcase className="meta-icon" />
                          {job.department}
                        </span>
                        <span className="job-meta-item">
                          <FaMapMarkerAlt className="meta-icon" />
                          {job.location}
                        </span>
                        <span className="job-meta-item">
                          <FaClock className="meta-icon" />
                          {job.type}
                        </span>
                      </div>
                    </div>
                    <div className="job-experience">
                      <FaUserTie className="experience-icon" />
                      {job.experience}
                    </div>
                  </div>
                  <p className="job-description">{job.description}</p>
                  <div className="job-requirements">
                    <h4>Key Requirements:</h4>
                    <ul>
                      {job.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="job-actions">
                    <a href={`mailto:careers@clubhosty.com?subject=Application for ${job.title}`} className="btn-apply">
                      Apply Now
                    </a>
                    <Link to="/contact" className="btn-learn-more">
                      Learn More
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-jobs">
                <p>No positions available for the selected filters. Please check back later or adjust your filters.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="career-process">
        <div className="container">
          <div className="section-header">
            <h2>Our Hiring Process</h2>
            <p>Simple, transparent, and designed to help us find the perfect fit</p>
          </div>
          <div className="process-timeline">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Apply Online</h3>
                <p>Submit your application and resume through our job portal or email</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Initial Screening</h3>
                <p>Our HR team reviews your application and conducts initial screening</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Technical Interview</h3>
                <p>Meet with the team for technical assessment and role discussion</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Final Round</h3>
                <p>Meet leadership team and discuss culture fit and expectations</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3>Offer & Onboarding</h3>
                <p>Receive offer and join our amazing team!</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="career-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Don't See a Perfect Match?</h2>
            <p>
              We're always looking for talented individuals. Send us your resume and let us know how you can contribute to ClubHosty's growth.
            </p>
            <div className="cta-actions">
              <a href="mailto:careers@clubhosty.com" className="btn-primary">
                <FaEnvelope className="btn-icon" />
                Send Your Resume
              </a>
              <Link to="/about" className="btn-secondary">
                Learn More About Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Career;
